/**
 * L'HORIZON DE FIABILITÉ FINANCIÈRE — la bascule Prothesis → Vevidental.
 *
 * Module à part, et pas une poignée de constantes dans `relances.ts` : ceci
 * n'est pas une règle de relance, c'est une déclaration sur la QUALITÉ de la
 * donnée. Les deux n'ont pas la même raison de changer — la règle bouge quand
 * le métier change d'avis, l'horizon ne bougera plus jamais, sauf si GéCéram
 * change à nouveau de logiciel.
 *
 * (Le découpage a été imposé par le cliquet de taille, qui a refusé de laisser
 * `relances.ts` dépasser 500 lignes. Le refus était juste.)
 */
import { z } from "zod";
import { SyncMetaSchema } from "./dto.js";
import { IdMetier, IsoDate, Uuid } from "./primitives.js";

/**
 * Le jour où GéCéram a quitté Prothesis Dental Solution pour Vevidental.
 *
 * Ce n'est PAS un seuil de confort, et surtout pas un âge glissant. Les
 * règlements enregistrés dans l'ancien logiciel ne sont pas passés dans le
 * nouveau : avant cette date, « facture impayée » ne prouve rien du tout.
 *
 * MESURÉ le 2026-08-12 sur les 7 751 factures de la copie, par la présence du
 * mode de paiement — que l'ancien système n'a pas transmis :
 *
 *     jusqu'à décembre 2023 ....   0,0 %  des factures ont un mode de paiement
 *     janvier 2024 ............    9,0 %  ← mois charnière, à moitié fiable
 *     FÉVRIER 2024 ...........    96,7 %  ← premier mois pleinement Vevidental
 *     mars 2024 ..............   100,0 %
 *
 * Effet mesuré sur la file du même jour : 1 957 cartes → 188, et l'étiquette
 * « critique » retrouve un sens (1 904 → 140).
 *
 * POURQUOI UNE DATE ET NON UN ÂGE. Un seuil « plus d'un an » aurait écarté
 * 61 cartes POSTÉRIEURES à la bascule pesant 331 655 € — de la dette réelle,
 * dont l'information de paiement est fiable. Un âge glissant en jetterait
 * d'autres chaque mois. Une date fixe énonce un fait, qui ne bouge plus.
 *
 * Rien n'est supprimé : ce qui est écarté est compté, nommé, et consultable
 * dans l'écran « Encours avant Vevidental » (sous-menu Finance).
 */
export const HORIZON_FIABILITE_FINANCE = "2024-02-01";

/**
 * Le mois charnière — à traiter à la main, pas par une règle.
 *
 * Janvier 2024 porte deux factures non soldées de 93 882 € et 36 682 €, pile
 * dans le mois où 9 % seulement des factures portent un mode de paiement.
 * Aucune règle automatique n'a de quoi trancher leur sort ; un humain, si.
 */
export const MOIS_CHARNIERE_FINANCE = {
  debut: "2024-01-01",
  fin: HORIZON_FIABILITE_FINANCE,
} as const;

/** Vrai si l'échéance précède la bascule — donc si le « impayé » ne prouve rien. */
export function avantBasculeVevidental(echeance: string | null): boolean {
  return echeance !== null && echeance < HORIZON_FIABILITE_FINANCE;
}

/** Vrai pour janvier 2024 — la zone grise, ni fiable ni négligeable. */
export function dansMoisCharniere(echeance: string | null): boolean {
  return echeance !== null && echeance >= MOIS_CHARNIERE_FINANCE.debut && echeance < MOIS_CHARNIERE_FINANCE.fin;
}

/** Ce que l'horizon met hors de portée de la règle (§7.C.4 bis). */
export const HeritageProthesisSchema = z.object({
  horizon: IsoDate,
  factures: z.number().int().min(0),
  resteDu: z.number(),
  cabinets: z.number().int().min(0),
});
export type HeritageProthesis = z.infer<typeof HeritageProthesisSchema>;

/** Une facture d'avant la bascule — donnée de règlement non exploitable. */
export const FactureHeriteeSchema = z.object({
  factureId: IdMetier,
  numeroFacture: z.string(),
  cabinetNom: z.string(),
  /** UUID Twenty quand la fiche existe, sinon null : la ligne reste visible. */
  cabinetId: Uuid.nullable(),
  dateFacture: IsoDate.nullable(),
  echeance: IsoDate.nullable(),
  resteDu: z.number().nullable(),
});
export type FactureHeritee = z.infer<typeof FactureHeriteeSchema>;

/**
 * Un COMPTE à vérifier — c'est l'unité de travail d'Estelle et d'Élodie.
 *
 * On regroupe par cabinet et non par facture : « vérifier 379 comptes » est une
 * tâche qu'on peut se répartir et cocher, « vérifier 1 764 factures » est un mur.
 */
export const CompteHeriteSchema = z.object({
  cabinetIdVevidental: IdMetier,
  cabinetNom: z.string(),
  cabinetId: Uuid.nullable(),
  factures: z.number().int().min(0),
  resteDu: z.number(),
  /** Échéance la plus ancienne et la plus récente du compte. */
  plusAncienne: IsoDate.nullable(),
  plusRecente: IsoDate.nullable(),
  /** Au moins une facture dans le mois charnière : à regarder en premier. */
  toucheMoisCharniere: z.boolean(),
});
export type CompteHerite = z.infer<typeof CompteHeriteSchema>;

/**
 * L'écran « Encours avant Vevidental ».
 *
 * Il lit la SOURCE — la copie Vevidental — et non les cartes du CRM : ce qu'on
 * demande à Estelle et Élodie de vérifier, ce sont des factures réelles, pas
 * des objets que le CRM aurait fabriqués puis rangés.
 */
export const EncoursAncienResponseSchema = z.object({
  heritage: HeritageProthesisSchema,
  /** TOUS les comptes concernés — aucune troncature silencieuse (§17.5). */
  comptes: z.array(CompteHeriteSchema),
  /** Le mois charnière, nommément : deux factures y pèsent 130 564 €. */
  charniere: z.object({
    debut: IsoDate,
    fin: IsoDate,
    factures: z.array(FactureHeriteeSchema),
    resteDu: z.number(),
  }),
  sync: SyncMetaSchema,
});
export type EncoursAncienResponse = z.infer<typeof EncoursAncienResponseSchema>;
